import {
  Box,
  Button,
  constants,
  Field,
  FieldMessage,
  FieldRow,
  FieldSet,
  Form,
} from '@redbubble/design-system';
import { defineMessages, useIntl } from '@redbubble/next/intl';
import { NodeItem } from 'pages/shop/types';
import { useState } from 'react';

interface CreateListSheetProps {
  onClose: () => void;
  handleSubmitForm: (name: string) => Promise<NodeItem | null | undefined>;
}

const messages = defineMessages({
  submitButtonLabel: {
    defaultMessage: 'Create',
  },
  nameFieldLabel: {
    defaultMessage: 'Name',
  },
  nameFieldPlaceholder: {
    defaultMessage: 'e.g. Gifts for Mom',
  },
  listNameRequired: {
    defaultMessage: 'Please fill in this field.',
  },
  listNameTooShort: {
    defaultMessage: 'Too short. Must be at least 3 characters long.',
  },
  listNameTooLong: {
    defaultMessage: 'Too long. Must be less than 50 characters long.',
  },
});

const validateListName = (value, intl) => {
  if (!value || (value && value.trim().length === 0))
    return intl.formatMessage(messages.listNameRequired);
  if (value.trim().length < 3)
    return intl.formatMessage(messages.listNameTooShort);
  if (value.trim().length > 50)
    return intl.formatMessage(messages.listNameTooLong);
  return null;
};

export const CreateListForm: React.FC<CreateListSheetProps> = ({
  onClose,
  handleSubmitForm,
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(false);

  const submitForm = async ({ name }, { resetForm }) => {
    setLoading(true);
    await handleSubmitForm(name);
    setLoading(false);
    resetForm();
    onClose();
  };

  return (
    <Form initialValues={{ name: '' }} onSubmit={submitForm}>
      {({ errors, touched }) => {
        return (
          <>
            <FieldSet>
              <FieldRow>
                <Field
                  label={intl.formatMessage(messages.nameFieldLabel)}
                  name="name"
                  type="text"
                  placeholder={intl.formatMessage(
                    messages.nameFieldPlaceholder
                  )}
                  fluid
                  intent={touched?.name && errors?.name && constants.ERROR}
                  validate={(name) => validateListName(name, intl)}
                >
                  {() =>
                    errors?.name && (
                      <FieldMessage intent={constants.ERROR}>
                        {errors.name}
                      </FieldMessage>
                    )
                  }
                </Field>
              </FieldRow>
            </FieldSet>

            <Box display="block">
              <Button
                type="submit"
                size={constants.MEDIUM}
                intent={constants.PRIMARY}
                strong
                loading={loading}
              >
                {intl.formatMessage(messages.submitButtonLabel)}
              </Button>
            </Box>
          </>
        );
      }}
    </Form>
  );
};
